import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Trash2, X, AlertTriangle } from 'lucide-react'
import { Button } from '../ui/button'
import { formatTimestamp } from '../../utilities/messageHelpers'

function DeleteConfirmDialog({ isOpen, onClose, onConfirm, isDeleting }) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Delete Message</h2>
          <Button onClick={onClose} variant="ghost" size="icon" disabled={isDeleting} aria-label="Close dialog">
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="p-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-900">
              This message will be permanently deleted for all participants and cannot be undone.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <Button onClick={onClose} variant="outline" disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  )
}

DeleteConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool.isRequired
}

function GroupMessageBubble({ message, isOwn, canDelete, onDelete }) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const body = message?.attributes?.body || message?.body || ''
  const createdAt = message?.attributes?.createdAt || message?.createdAt
  const deletedAt = message?.attributes?.deletedAt || message?.deletedAt
  const sender = message?.attributes?.sender || message?.sender || {}
  const senderName = sender.preferredName || sender.fullName || 'Unknown'

  const handleDeleteConfirm = async () => {
    setIsDeleting(true)
    try {
      await onDelete(message.id)
      setShowDeleteDialog(false)
    } catch {
    } finally {
      setIsDeleting(false)
    }
  }

  if (deletedAt) {
    return (
      <div className={`max-w-[70%] ${isOwn ? 'ml-auto' : 'mr-auto'} mb-2`}>
        {!isOwn && (
          <p className="text-xs text-gray-400 mb-0.5 truncate px-1">{senderName}</p>
        )}
        <div className="rounded-lg px-4 py-2 bg-gray-50 border border-gray-200">
          <p className="text-sm italic text-gray-400">Message deleted</p>
        </div>
        <div className={`flex items-center mt-1 px-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className="text-xs text-gray-400">{formatTimestamp(createdAt)}</span>
        </div>
      </div>
    )
  }

  const bubbleClasses = isOwn
    ? 'bg-purple-100 text-purple-900 ml-auto'
    : 'bg-gray-100 text-gray-900 mr-auto'

  return (
    <>
      <div
        className={`max-w-[70%] ${isOwn ? 'ml-auto' : 'mr-auto'} mb-2 group`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!isOwn && (
          <p className="text-xs text-gray-500 mb-0.5 truncate px-1">{senderName}</p>
        )}
        <div className={`relative flex items-end gap-1 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
          <div className={`rounded-lg px-4 py-2 ${bubbleClasses}`}>
            <p className="text-sm whitespace-pre-wrap break-words">{body}</p>
          </div>
          {canDelete && isHovered && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              aria-label="Delete message"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className={`flex items-center mt-1 px-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className="text-xs text-gray-500">{formatTimestamp(createdAt)}</span>
        </div>
      </div>

      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </>
  )
}

GroupMessageBubble.propTypes = {
  message: PropTypes.object.isRequired,
  isOwn: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool,
  onDelete: PropTypes.func
}

GroupMessageBubble.defaultProps = {
  canDelete: false,
  onDelete: () => {}
}

export default GroupMessageBubble
